import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Button, 
  MenuItem, 
  Select, 
  AppBar, 
  Toolbar, 
  Box, 
  Grid, 
  Typography, 
  TextField,
  FormControlLabel,
  Checkbox,
  FormGroup
} from '@mui/material';
import { getFleets, getFleet, getPrice, getOperatorData, createCheckoutSession, createReservation, checkVehicleAvailable, getCoupon, getReservationSurcharges, getAvailableVehicleTypes, getAvailableAssetTypes } from '../api';
import DOMPurify from 'dompurify';
import { addDays } from 'date-fns';
import { toFleetWallClock } from '../utils/fleetTime';
import LoginDialog from '../components/LoginDialog';
import RentalDetails from '../components/RentalDetails';
import DateRangePicker from '../components/DateRangePicker';

// ACOS: format an hour (0-23) as a 12-hour label for the hourly time drop-downs.
const formatHour = (h) => {
  const ampm = h < 12 ? 'AM' : 'PM';
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:00 ${ampm}`;
};

const ReservationPage = () => {
  const navigate = useNavigate(); 
  const loc = useLocation();
  const [fleets, setFleets] = useState([]); 
  const [vehicleTypes, setVehicleTypes] = useState([]); 
  const [availableVehicleTypes, setAvailableVehicleTypes] = useState([]);
  // ACOS: asset offerings (unmonetized, no price) surfaced alongside vehicle types. An asset-only
  // fleet (e.g. fleet 34 "BallyQ Reservations") renders these instead of vehicle cards.
  const [availableAssetTypes, setAvailableAssetTypes] = useState([]);
  // Selected asset offering: { asset_type_id, operator_reservation_id, asset_type_name,
  // description, min_increment_minutes } — null when a vehicle (or nothing) is selected.
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [isLoadingVehicleTypes, setIsLoadingVehicleTypes] = useState(false);
  const [vehicleTypeError, setVehicleTypeError] = useState(null);
  const [vehicleImage, setVehicleImage] = useState([]);
  const [selectedVehicleType, setSelectedVehicleType] = useState(''); 
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 800);
  const [selectedVehicle, setSelectedVehicle] = useState(null); 
  const [dateRangeSelected, setDateRangeSelected] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedVehicleName, setSelectedVehicleName] = useState([]);
  const [priceDetails, setPriceDetails] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [couponCode, setCouponCode] = useState('');
  const [couponId, setCouponId] = useState(null);
  const [couponDescription, setCouponDescription] = useState('');
  const [hasCoupon, setHasCoupon] = useState(false);
  const [couponPercent, setCouponPercent] = useState(null);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [couponError, setCouponError] = useState('');
  
  // New state variables for note functionality
  const [noteText, setNoteText] = useState('');
  const [hasNote, setHasNote] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  // Add-on state variables
  const [addons, setAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false);
  
  // Quantity state - tracks selected quantity for each vehicle type
  const [vehicleQuantities, setVehicleQuantities] = useState({});
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  // ACOS: per-fleet opt-in. Daily mode (default) = date-only calendar days (legacy). Hourly
  // mode = date + hour pickers with duration-based pricing (< 24h hourly; >= 24h daily ceil(hours/24)).
  const [hourlyEnabled, setHourlyEnabled] = useState(false);
  // ACOS: unmonetized fleets (fleets.is_monetized === false) book WITHOUT payment — no Stripe,
  // no pricing UI, straight to confirmation (after any required waiver). Defaults to monetized
  // (true) so the paid path is unchanged unless the loaded fleet explicitly opts out.
  const [isMonetized, setIsMonetized] = useState(true);
  const [pickupHour, setPickupHour] = useState(9);
  const [dropoffHour, setDropoffHour] = useState(17);
  // ACOS: fleet default — drop-off is on the day AFTER pick-up (e.g. 9am -> 9am next day = 24h).
  const [nextDayDefault, setNextDayDefault] = useState(false);
  // ACOS: fleet weekly operating hours (fleet-local). When set, the pickers prevent selecting
  // closed weekdays and out-of-hours times. Blackout dates are enforced by the backend instead.
  const [operatingHours, setOperatingHours] = useState(null);

  // Effective pick-up / drop-off instants: dates from the calendar, plus the chosen hour when
  // hourly is enabled (otherwise local midnight = legacy calendar-day behavior).
  const withHour = (date, hour) => {
    if (!date) return null;
    const d = new Date(date);
    d.setHours(hourlyEnabled ? hour : 0, 0, 0, 0);
    return d;
  };
  const effectiveStart = withHour(selectedStartDate, pickupHour);
  let effectiveEnd = withHour(selectedEndDate, dropoffHour);
  // Hourly: if drop-off lands at/before pick-up (e.g. same day, 9am -> 9am), roll it to the
  // next day — that's a 24h rental. Only applies when a single day is picked (or an inverted window).
  if (hourlyEnabled && effectiveStart && effectiveEnd && effectiveEnd.getTime() <= effectiveStart.getTime()) {
    effectiveEnd = addDays(effectiveEnd, 1);
  }

  const durationMinutes = (hourlyEnabled && effectiveStart && effectiveEnd && effectiveEnd > effectiveStart)
    ? Math.round((effectiveEnd.getTime() - effectiveStart.getTime()) / 60000)
    : 0;
  const minIncrement = hourlyEnabled && durationMinutes > 0 ? (durationMinutes < 1440 ? 60 : 1440) : null;
  const durationMode = hourlyEnabled; // duration billing only applies in hourly mode
  const rateUnits = minIncrement ? Math.max(1, Math.ceil(durationMinutes / minIncrement)) : 0;
  const rateLabel = minIncrement === 60
    ? `${rateUnits} hour${rateUnits > 1 ? 's' : ''} — hourly rate`
    : minIncrement === 1440
      ? `${rateUnits} day${rateUnits > 1 ? 's' : ''} — daily rate`
      : '';

  // ACOS operating-hours picker constraints. Times/weekdays are treated as fleet-local; for the
  // pilot the rider is on-site (browser tz ≈ fleet tz). Blackout dates are NOT disabled here —
  // the backend rejects those at "Find Vehicles" with a clear reason.
  const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const ohToMin = (hhmm) => { const [h, m] = String(hhmm).split(':').map(Number); return h * 60 + (m || 0); };
  const hasOperatingHours = !!(operatingHours && Object.keys(operatingHours).length > 0);
  const dayKeyOf = (date) => (date ? WEEKDAY_KEYS[new Date(date).getDay()] : null);
  const isDayDisabled = (date) => hasOperatingHours && !!date && !operatingHours[dayKeyOf(date)];
  const allowedHoursFor = (date) => {
    if (!hasOperatingHours || !date) return Array.from({ length: 24 }, (_, h) => h);
    const day = operatingHours[dayKeyOf(date)];
    if (!day || !day.open || !day.close) return [];
    const oMin = ohToMin(day.open);
    const cMin = ohToMin(day.close);
    return Array.from({ length: 24 }, (_, h) => h).filter((h) => h * 60 >= oMin && h * 60 <= cMin);
  };
  const pickupHoursAllowed = allowedHoursFor(selectedStartDate);
  const dropoffHoursAllowed = allowedHoursFor(selectedEndDate);
  // Move an hour to the NEAREST valid hour in the operating window: below-open clamps up to
  // open, past-close clamps down to close. Keeps a 9am->9am default coherent when a fleet opens
  // at 10 (becomes 10am->10am next day = 24h) instead of splaying to open/close (10am->6pm).
  const clampHour = (hour, allowed) => {
    if (!allowed.length || allowed.includes(hour)) return hour;
    return hour < allowed[0] ? allowed[0] : allowed[allowed.length - 1];
  };

  // Clamp the chosen hours into the operating window when the fleet, dates, or hours change.
  useEffect(() => {
    if (!hourlyEnabled || !hasOperatingHours) return;
    const cp = clampHour(pickupHour, pickupHoursAllowed);
    if (cp !== pickupHour) setPickupHour(cp);
    const cd = clampHour(dropoffHour, dropoffHoursAllowed);
    if (cd !== dropoffHour) setDropoffHour(cd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourlyEnabled, operatingHours, selectedStartDate, selectedEndDate]);

  const handleViewPrices = useCallback(async (overrideCouponId = null) => {
    try {
      if (!selectedVehicleType) {
        alert("Please select a vehicle type");
        return;
      }
      
      if (!dateRangeSelected) {
        // Don't fetch price if no date range has been selected
        return;
      }
      
      const fleet = JSON.parse(localStorage.getItem('fleet'));
      // Apply the chosen hour when hourly is enabled; otherwise local midnight (legacy days).
      const s = new Date(selectedStartDate); s.setHours(hourlyEnabled ? pickupHour : 0, 0, 0, 0);
      const e = new Date(selectedEndDate); e.setHours(hourlyEnabled ? dropoffHour : 0, 0, 0, 0);
      const start_date = toFleetWallClock(s);
      const end_date = toFleetWallClock(e);
      setStartDate(start_date);
      setEndDate(end_date);

      // Use the override couponId if provided, otherwise use the state value
      const couponIdToUse = overrideCouponId !== null ? overrideCouponId : couponId;

      // Fetch price details. In hourly mode duration_mode bills by elapsed time (daily = ceil(hours/24)).
      const response = await getPrice(fleet.id, start_date, end_date, selectedVehicleType, couponIdToUse, selectedAddons, selectedQuantity, minIncrement, durationMode);

      // If coupon was rejected (expired or max uses reached), show inline error and allow retry
      if (response.coupon_rejected) {
        setCouponDescription('');
        setCouponPercent(null);
        setHasCoupon(false);
        setSubmitDisabled(false);
        setCouponError('This coupon has expired or reached its maximum number of uses.');
        // Clear couponId last to avoid re-triggering useEffect chain before other state is set
        setTimeout(() => setCouponId(null), 0);
      }

      setPriceDetails(response);
      
      // Now that we have price details with fleet_fare_id, fetch the add-ons
      if (response && response.fleet_fare_id) {
        const operatorParam = new URLSearchParams(loc.search).get('operator');
        getReservationSurcharges(operatorParam, response.fleet_fare_id)
          .then((surcharges) => {
            // The response is already an array of surcharges
            // Filter surcharges to get addons (surcharge_type === 'reservation_addon')
            const addonSurcharges = Array.isArray(surcharges) ? 
              surcharges.filter(surcharge => surcharge.surcharge_type === 'reservation_addon') : 
              [];
            setAddons(addonSurcharges);
          })
          .catch((error) => console.error('Failed to load surcharges:', error));
      }
      
    } catch (error) {
      console.error("Error fetching price details:", error);
    }
  }, [selectedVehicleType, selectedStartDate, selectedEndDate, dateRangeSelected, couponId, selectedAddons, selectedQuantity, loc.search, minIncrement, durationMode, hourlyEnabled, pickupHour, dropoffHour]);

  useEffect(() => {
    const handleResize = () => {
      const currentIsMobile = window.innerWidth <= 800;
      setIsMobile(currentIsMobile); 
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);  
  // Update document title with fleet account name
  useEffect(() => {
    const fleet = JSON.parse(localStorage.getItem('fleet')) || {};
    if (fleet.account_name) {
      document.title = fleet.account_name;
    } else {
      document.title = 'Roamr Reservations'; // fallback title
    }
  }, []); // Run only once on mount

  // Load operator + fleet data ONCE per fleet/operator param. Kept separate from the price
  // fetch below: re-fetching the fleet on every date change would re-seed hourly defaults +
  // operating_hours (a fresh object ref each time), which fought the clamp effect and looped.
  useEffect(() => {
    const queryParams = new URLSearchParams(loc.search);
    const fleetParam = queryParams.get('fleet');
    const operatorParam = queryParams.get('operator');

    if (operatorParam) {
      getOperatorData(operatorParam).then((operatorResponse) => {
        localStorage.setItem('operator', JSON.stringify(operatorResponse))
      });
      getFleets(operatorParam)
        .then((data) => setFleets(data.fleets))
        .catch((error) => console.error('Failed to load fleets:', error));

      // Get the fleet data if fleetParam is available
      if (fleetParam) {
        getFleet(fleetParam)
          .then((fleetData) => {
            const fleet = fleetData?.fleet;
            if (fleet) {
              localStorage.setItem('fleet', JSON.stringify(fleet));
              setVehicleTypes(fleet.vehicle_types || []);
              setHourlyEnabled(!!fleet.hourly_reservations_enabled);
              // ACOS: unmonetized fleets skip payment entirely (is_monetized === false).
              setIsMonetized(fleet.is_monetized !== false);
              const hd = fleet.hourly_defaults || {};
              if (Number.isInteger(hd.pickup_hour)) setPickupHour(hd.pickup_hour);
              if (Number.isInteger(hd.dropoff_hour)) setDropoffHour(hd.dropoff_hour);
              setNextDayDefault(!!hd.dropoff_next_day);
              setOperatingHours(fleet.operating_hours || null);
            }
          })
          .catch((error) => console.error('Failed to fetch fleet data:', error));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search]);

  // Fetch pricing whenever the window or selected vehicle changes.
  // ACOS: unmonetized fleets have no pricing — never call the price API (it would 404/no-fare).
  useEffect(() => {
    if (isMonetized && selectedStartDate && selectedEndDate && selectedVehicleType) {
      handleViewPrices();
    }
  }, [isMonetized, selectedStartDate, selectedEndDate, selectedVehicleType, handleViewPrices]);

  // ACOS: asset offerings have no price API, so the pricing effect above (which sets startDate/
  // endDate) never runs for them. Compute the effective ISO window here — applying the chosen
  // hour in hourly mode, else local midnight — so the booking payload has concrete dates.
  useEffect(() => {
    if (!selectedAsset || !selectedStartDate || !selectedEndDate) return;
    const s = new Date(selectedStartDate); s.setHours(hourlyEnabled ? pickupHour : 0, 0, 0, 0);
    const e = new Date(selectedEndDate); e.setHours(hourlyEnabled ? dropoffHour : 0, 0, 0, 0);
    setStartDate(toFleetWallClock(s));
    setEndDate(toFleetWallClock(e));
  }, [selectedAsset, selectedStartDate, selectedEndDate, hourlyEnabled, pickupHour, dropoffHour]);



  const handleFleetChange = async (event) => {
    const selectedFleetId = event.target.value;
    try {
      const fleetData = await getFleet(selectedFleetId);
      const fleet = fleetData?.fleet;
      if (fleet) {
        localStorage.setItem('fleet', JSON.stringify(fleet));
        setVehicleTypes(fleet.vehicle_types || []);
        setHourlyEnabled(!!fleet.hourly_reservations_enabled);
        const hd = fleet.hourly_defaults || {};
        if (Number.isInteger(hd.pickup_hour)) setPickupHour(hd.pickup_hour);
        if (Number.isInteger(hd.dropoff_hour)) setDropoffHour(hd.dropoff_hour);
        setNextDayDefault(!!hd.dropoff_next_day);
        setOperatingHours(fleet.operating_hours || null);
      }
    } catch (error) {
      console.error('Failed to fetch and set fleet data:', error);
    }
  };

  // ACOS: pick-up / drop-off DATES come from the calendar (DateRangePicker). Hours are
  // separate (hourly mode only). Resets the downstream flow whenever the dates change.
  const handleDateRangeChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    // ACOS: when the fleet defaults to next-day drop-off (e.g. a 24h 9am->9am rental), a
    // single-day pick should visibly land the drop-off on the following day.
    const sameCalendarDay = newStartDate && newEndDate
      && new Date(newStartDate).toDateString() === new Date(newEndDate).toDateString();
    if (hourlyEnabled && nextDayDefault && sameCalendarDay) {
      newEndDate = new Date(new Date(newStartDate).getTime() + 24 * 60 * 60 * 1000);
    }
    setSelectedStartDate(newStartDate);
    setSelectedEndDate(newEndDate);
    setDateRangeSelected(!!(newStartDate && newEndDate));
    // Clear available vehicles when the window changes - user must click Find Vehicles
    setAvailableVehicleTypes([]);
    setAvailableAssetTypes([]);
    setSelectedAsset(null);
    setVehicleTypeError(null);
    // Reset the entire flow - clear vehicle selection and all related state
    setSelectedVehicleType('');
    setSelectedVehicle(null);
    setVehicleImage([]);
    setSelectedVehicleName([]);
    setPriceDetails(null);
    setSelectedQuantity(1);
    // Clear add-ons and coupon
    setAddons([]);
    setSelectedAddons([]);
    setCouponCode('');
    setCouponId(null);
    setCouponDescription('');
    setHasCoupon(false);
    setCouponPercent(null);
    setSubmitDisabled(false);
    // Clear note
    setNoteText('');
    setHasNote(false);
    setNoteContent('');
  };

  const handleFindVehicles = async () => {
    const fleet = JSON.parse(localStorage.getItem('fleet'));
    
    if (!selectedStartDate || !selectedEndDate) {
      alert('Please select a date range first');
      return;
    }
    
    if (fleet && fleet.id) {
      setIsLoadingVehicleTypes(true);
      setVehicleTypeError(null);
      setAvailableVehicleTypes([]);
      setAvailableAssetTypes([]);
      // Clear current selection
      setSelectedVehicleType('');
      setSelectedVehicle(null);
      setSelectedAsset(null);
      setVehicleImage([]);
      setSelectedVehicleName([]);
      setPriceDetails(null);

      const startIso = toFleetWallClock(effectiveStart || selectedStartDate);
      const endIso = toFleetWallClock(effectiveEnd || selectedEndDate);

      // ACOS: fetch vehicle types AND asset offerings together. Either may be empty; a fleet can
      // be vehicle-only, asset-only (e.g. fleet 34), or both. Failures are handled independently
      // so an asset-only fleet (no vehicle types) still surfaces its asset cards, and vice versa.
      const [vehicleResult, assetResult] = await Promise.allSettled([
        getAvailableVehicleTypes(fleet.id, startIso, endIso, minIncrement),
        getAvailableAssetTypes(fleet.id, startIso, endIso, minIncrement),
      ]);

      const availableTypes = vehicleResult.status === 'fulfilled' && Array.isArray(vehicleResult.value)
        ? vehicleResult.value : [];
      const availableAssets = assetResult.status === 'fulfilled' && Array.isArray(assetResult.value)
        ? assetResult.value : [];

      console.log('handleFindVehicles - Available vehicle types:', availableTypes);
      console.log('handleFindVehicles - Available asset offerings:', availableAssets);

      setAvailableVehicleTypes(availableTypes);
      setAvailableAssetTypes(availableAssets);

      if (availableTypes.length > 0 || availableAssets.length > 0) {
        setVehicleTypeError(null);
      } else {
        // Prefer the backend's specific reason (e.g. outside operating hours / blackout day) when
        // both lookups failed; otherwise fall back to a generic "nothing available" message.
        const reason = (vehicleResult.status === 'rejected' && vehicleResult.reason && vehicleResult.reason.message)
          || (assetResult.status === 'rejected' && assetResult.reason && assetResult.reason.message)
          || 'Nothing is available for the selected dates. Please try a different date range or contact the operator.';
        setVehicleTypeError(reason);
      }

      setIsLoadingVehicleTypes(false);
    }
  };

  // ACOS: select an asset offering (unmonetized). Sets the parallel asset selection state and
  // clears any vehicle selection so the two selection paths stay mutually exclusive.
  const handleAssetSelection = (offering) => {
    setSelectedVehicleType('');
    setSelectedVehicle(null);
    setVehicleImage([]);
    setSelectedVehicleName([]);
    setPriceDetails(null);
    setSelectedAsset({
      kind: 'asset',
      asset_type_id: offering.asset_type_id,
      operator_reservation_id: offering.operator_reservation_id,
      asset_type_name: offering.asset_type_name,
      description: offering.description || '',
      min_increment_minutes: offering.min_increment_minutes || null,
      image_url_large: offering.image_url_large || null,
      image_url_small: offering.image_url_small || null,
    });
  };

  const handleVehicleSelection = (vehicleTypeId) => {
    setSelectedVehicleType(vehicleTypeId);
    const selectedVehicle = vehicleTypes.find(vehicle => vehicle.vehicle_type_id === vehicleTypeId);
    if (selectedVehicle) {
      const vehicle_image = selectedVehicle.features.vehicle_image;
      setVehicleImage(vehicle_image);
      setSelectedVehicle(selectedVehicle); 
      setSelectedVehicleName(selectedVehicle.vehicle_type_name);
      
      // Set the selected quantity from the dropdown (default to 1 if not set)
      const quantity = vehicleQuantities[vehicleTypeId] || 1;
      setSelectedQuantity(quantity);
    }
  };  
  
  const handleReserveClick = async () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); 

    if (selectedStartDate < today) {
      alert("The start date cannot be in the past. Please select a valid date range.");
      return;
    }

    // ACOS: asset offerings have no vehicle-type precheck endpoint. The create-reservation call
    // does its own availability gating (409 on conflict), which is surfaced there — so for an
    // asset offering we go straight to login/booking.
    if (selectedAsset) {
      setIsLoginDialogOpen(true);
      return;
    }

    try {
      await checkVehicleAvailable(selectedVehicleType, fleet.id, startDate, endDate, minIncrement);
      setIsLoginDialogOpen(true);
    } catch (error) {
      alert(error.message);
    }
  };
  
  // Handle add-on selection/deselection
  const handleAddonChange = (addonId) => {
    setSelectedAddons(prev => {
      const newSelectedAddons = prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId];
      
      return newSelectedAddons;
    });
  };

  const handleSubmitCoupon = async () => {
    setCouponError('');
    try {
      const response = await getCoupon(fleet.id, couponCode);

      if (response && response.ok) {
        const data = await response.json();
        if (Array.isArray(data) && data.length === 0) {
          setCouponError('No coupon found for the provided code.');
        } else if (data && data[0].description && data[0].discount_pct) {
          const newCouponId = data[0].id;
          setCouponDescription(data[0].description);
          setCouponPercent(data[0].discount_pct);
          setCouponId(newCouponId);
          setHasCoupon(true);
          setSubmitDisabled(true);
          // Pass the couponId directly to handleViewPrices to avoid state timing issues
          handleViewPrices(newCouponId);
        } else {
          setCouponError('This coupon code is not valid.');
        }
      } else {
        setCouponError('Unable to validate coupon. Please try again.');
      }
    } catch (error) {
      console.error('Coupon submission error:', error);
      setCouponError('Unable to validate coupon. Please try again.');
    }
  };  
  const fleet = JSON.parse(localStorage.getItem('fleet')) || {};
  const requiredParams = new URLSearchParams(loc.search);
  const hasRequiredParams = requiredParams.get('operator') && requiredParams.get('fleet');

  if (!hasRequiredParams) {
    return (
      <div style={{
        backgroundColor: '#f7f9fc',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '500px',
          padding: '40px',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          <h1 style={{ fontSize: '24px', color: '#333', marginBottom: '16px' }}>
            Reservation Page Not Available
          </h1>
          <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.6', marginBottom: '24px' }}>
            This reservation page requires a valid booking link from your rental provider.
            Please check the link you were given or contact your rental provider for assistance.
          </p>
          <p style={{ fontSize: '14px', color: '#999' }}>
            If you believe this is an error, please verify the URL includes the required operator and fleet information.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#f7f9fc',
      minHeight: '100vh',
      backgroundImage: fleet.reservation_hero_img ? `url(${fleet.reservation_hero_img})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      backgroundRepeat: 'no-repeat'
    }}>
      <AppBar position="static" style={{ backgroundColor: fleet.menu_bkg_color || '#1a73e8' }}>
        <Toolbar>
          <Box
            component="img"
            src={fleet.web_logo || 'default_logo.png'}
            alt="Menu Logo"
            sx={{
              backgroundColor: fleet.web_bkg_color || '#fff',
              padding: '8px',
              borderRadius: '8px',
              height: '125px',
              objectFit: 'contain',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          />
        </Toolbar>
      </AppBar>
      <Box mt={4} mx="auto" width={isMobile ? "90%" : "100%"} maxWidth="1400px" pt={4} px={0}>
        <Grid container spacing={isMobile ? 1 : 2}>
          <Grid 
            item
            xs={12}
            md={4}
            lg={3}
            style={{ 
              padding: isMobile ? 0 : '0 10px 10px 0',
              position: 'relative',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Fleet Selector */}
            {fleets.length > 1 ? (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  padding: isMobile ? '9px' : '15px',
                  position: 'relative',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  borderRadius: '10px',
                  overflow: 'visible',
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                  Select Fleet Location
                </Typography>
                <Select
                  value={fleets.some(f => f.id === fleet.id) ? fleet.id : ''}
                  onChange={handleFleetChange}
                  disabled={!fleet.id}
                  sx={{ 
                    width: '100%',
                    bgcolor: 'white', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  {fleets.map(fleet => (
                    <MenuItem key={fleet.id} value={fleet.id}>
                      {fleet.account_name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            ) : fleets.length === 1 ? (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  padding: isMobile ? '9px' : '15px',
                  position: 'relative',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  borderRadius: '10px',
                  overflow: 'visible',
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: '#333',
                    textAlign: 'center'
                  }}
                >
                  {fleet.account_name}
                </Typography>
              </Box>
            ) : null}

            {/* Date Range Picker */}
            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                margin: 0,
                padding: isMobile ? '9px' : '15px',
                position: 'relative',
                backgroundColor: '#fff',
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                borderRadius: '10px',
                overflow: 'visible',
                marginBottom: '1.5rem',
                border: '1px solid rgba(0,0,0,0.05)'
              }}
            >
              {/* ACOS: dates via the calendar; hour drop-downs only when the fleet enables hourly */}
              <Box sx={{ mb: 2 }}>
                <DateRangePicker
                  startDate={selectedStartDate}
                  endDate={selectedEndDate}
                  onChange={handleDateRangeChange}
                  minDate={new Date()}
                  isDayDisabled={isDayDisabled}
                />
                {hourlyEnabled && (
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Pick-up time</Typography>
                      <Select value={pickupHoursAllowed.includes(pickupHour) ? pickupHour : ''} onChange={(e) => { setPickupHour(Number(e.target.value)); setPriceDetails(null); }} size="small" fullWidth displayEmpty>
                        {pickupHoursAllowed.length === 0 && <MenuItem value="" disabled>Closed this day</MenuItem>}
                        {pickupHoursAllowed.map((h) => (<MenuItem key={h} value={h}>{formatHour(h)}</MenuItem>))}
                      </Select>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Drop-off time</Typography>
                      <Select value={dropoffHoursAllowed.includes(dropoffHour) ? dropoffHour : ''} onChange={(e) => { setDropoffHour(Number(e.target.value)); setPriceDetails(null); }} size="small" fullWidth displayEmpty>
                        {dropoffHoursAllowed.length === 0 && <MenuItem value="" disabled>Closed this day</MenuItem>}
                        {dropoffHoursAllowed.map((h) => (<MenuItem key={h} value={h}>{formatHour(h)}</MenuItem>))}
                      </Select>
                    </Grid>
                  </Grid>
                )}
                {hourlyEnabled && rateLabel && (
                  <Typography sx={{ mt: 1.5, fontWeight: 600, color: '#00B894' }}>{rateLabel}</Typography>
                )}
                {hourlyEnabled && selectedStartDate && selectedEndDate && durationMinutes === 0 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#d32f2f' }}>
                    Drop-off must be after pick-up.
                  </Typography>
                )}
                {minIncrement === 60 && availableVehicleTypes.length > 0 && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#666' }}>
                    Under 24 hours — showing only vehicle types with an hourly rate available.
                  </Typography>
                )}
              </Box>
              
              {/* Find Vehicles Button */}
              <Button
                onClick={handleFindVehicles}
                variant="contained"
                disabled={!dateRangeSelected || isLoadingVehicleTypes}
                sx={{
                  mt: 2,
                  width: '100%',
                  bgcolor: '#00B894',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,184,148,0.3)',
                  fontSize: isMobile ? '15px' : '16px',
                  fontWeight: 600,
                  py: isMobile ? 1.8 : 1.5,
                  minHeight: isMobile ? '50px' : 'auto',
                  '&:hover': {
                    backgroundColor: '#00A983',
                  },
                  '&:disabled': {
                    backgroundColor: '#cccccc',
                    color: '#666',
                  }
                }}
              >
                {isLoadingVehicleTypes ? 'Searching...' : 'Find Available Vehicles'}
              </Button>
            </Box>
            
            <div style={{ height: '20px' }} />
            
            {/* Add-ons Section - Only show when vehicle is selected and dates are set */}
            {selectedVehicleType && dateRangeSelected && addons.length > 0 && (
              <Box 
                sx={{ 
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  padding: isMobile ? '9px' : '15px',
                  position: 'relative',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  borderRadius: '10px',
                  overflow: 'visible',
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(0,0,0,0.05)',
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-start',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>Add-ons</Typography>
                
                {addons.map((addon) => (
                  <FormGroup sx={{ width: '100%' }} key={addon.id}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={selectedAddons.includes(addon.id)}
                          onChange={() => handleAddonChange(addon.id)}
                          sx={{
                            color: '#00B894',
                            '&.Mui-checked': {
                              color: '#00B894',
                            },
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {addon.description} - {addon.percent ? `${addon.percent * 100}%` : `$${(addon.amount / 100).toFixed(2)} ${addon.charge_type === 'daily' ? '(daily)' : ''}`}
                          </Typography>
                        </Box>
                      }
                    />
                  </FormGroup>
                ))}
              </Box>
            )}
            
            <div style={{ height: '20px' }} />
            
            {/* Coupon Section - Only show when vehicle is selected and dates are set */}
            {selectedVehicleType && dateRangeSelected && (
              <Box 
                sx={{ 
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  padding: isMobile ? '9px' : '15px',
                  position: 'relative',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  borderRadius: '10px',
                  overflow: 'visible',
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(0,0,0,0.05)',
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-start',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>Enter Coupon</Typography>
              <TextField
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value);
                  setCouponError('');
                  if (submitDisabled && !hasCoupon) {
                    setSubmitDisabled(false);
                  }
                }}
                placeholder="Enter your coupon code"
                fullWidth
                sx={{ 
                  marginTop: 1, 
                  marginBottom: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#00B894'
                    }
                  }
                }}
              />
              <Button
                onClick={handleSubmitCoupon}
                variant="contained"
                color="primary"
                disabled={submitDisabled}
                sx={{ 
                  mt: 2,
                  bgcolor: '#00B894',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,184,148,0.3)',
                  '&:hover': {
                    backgroundColor: '#00A983',
                  },
                  '&:disabled': {
                    backgroundColor: '#cccccc',
                  }
                }}
              >
                Apply Coupon
              </Button>

              {couponError && (
                <Typography
                  variant="body1"
                  sx={{
                    mt: 2,
                    color: '#d32f2f',
                    bgcolor: 'rgba(211,47,47,0.1)',
                    p: 1,
                    borderRadius: '6px',
                    width: 'calc(100% - 8px)',
                    fontWeight: 500,
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                  }}
                >
                  {couponError}
                </Typography>
              )}
              {hasCoupon && !couponError && (
                <Typography
                  variant="body1"
                  sx={{
                    mt: 2,
                    color: "#00B894",
                    bgcolor: 'rgba(0,184,148,0.1)',
                    p: 1,
                    borderRadius: '6px',
                    width: 'calc(100% - 8px)',
                    fontWeight: 500,
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                  }}
                >
                  Coupon added: {couponDescription}
                </Typography>
              )}
              </Box>
            )}
            
            <div style={{ height: '20px' }} />
            
            {/* Note Entry Box - Only show when vehicle is selected and dates are set */}
            {selectedVehicleType && dateRangeSelected && (
              <Box 
                sx={{ 
                  width: '100%',
                  maxWidth: '100%',
                  margin: 0,
                  padding: isMobile ? '9px' : '15px',
                  position: 'relative',
                  backgroundColor: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  borderRadius: '10px',
                  overflow: 'visible',
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(0,0,0,0.05)',
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-start',
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
                  {selectedVehicle?.features?.notes_title || 'Add a Note'}
                </Typography>
              <TextField
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder={selectedVehicle?.features?.notes_placeholder || "Add any special requests or notes here"}
                multiline
                minRows={2}
                maxRows={10}
                fullWidth
                sx={{ 
                  marginTop: 1, 
                  marginBottom: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#00B894'
                    }
                  },
                  '& .MuiInputBase-root': {
                    minHeight: '56px',
                    alignItems: 'flex-start'
                  },
                  '& .MuiOutlinedInput-input': {
                    overflow: 'auto',
                    maxHeight: 'unset',
                    lineHeight: 1.5
                  }
                }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'row', mt: 2 }}>
                <Button
                  onClick={() => {
                    if (noteText.trim()) {
                      setNoteContent(noteText.trim());
                      setHasNote(true);
                    }
                  }}
                  variant="contained"
                  color="primary"
                  disabled={noteText.trim() === ''} // Only disable when the text field is empty
                  sx={{ 
                    bgcolor: '#00B894',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,184,148,0.3)',
                    '&:hover': {
                      backgroundColor: '#00A983',
                    },
                    '&:disabled': {
                      backgroundColor: '#cccccc',
                    }
                  }}
                >
                  {hasNote ? 'Update Note' : 'Add Note'}
                </Button>
                
                {hasNote && (
                  <Button
                    onClick={() => {
                      setNoteContent('');
                      setNoteText('');
                      setHasNote(false);
                    }}
                    variant="outlined"
                    sx={{ 
                      ml: 2,
                      color: '#ff5252',
                      borderColor: '#ff5252',
                      borderRadius: '8px',
                      '&:hover': {
                        backgroundColor: 'rgba(255,82,82,0.08)',
                        borderColor: '#ff3232',
                      }
                    }}
                  >
                    Remove Note
                  </Button>
                )}
              </Box>
              
              {hasNote && (
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mt: 2, 
                    color: "#00B894", 
                    bgcolor: 'rgba(0,184,148,0.1)', 
                    p: 1,
                    borderRadius: '6px', 
                    width: 'calc(100% - 8px)',
                    fontWeight: 500,
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                  }}
                >
                  Note added to rental
                </Typography>
              )}
              </Box>
            )}
          </Grid>
          
          <Grid 
            item
            xs={12}
            md={8}
            lg={9}
            style={{ 
              padding: isMobile ? 0 : '0 0 0 32px',
              position: 'relative',
              zIndex: 900
            }}
          >
            {/* Show instructions when no search has been done yet */}
            {availableVehicleTypes.length === 0 && availableAssetTypes.length === 0 && !vehicleTypeError && !selectedVehicleType && !selectedAsset ? (
              <Box
                sx={{ 
                  width: '100%',
                  maxWidth: '700px',
                  margin: '0 auto',
                  backgroundColor: '#ffffff',
                  borderRadius: '10px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  p: isMobile ? 1 : 4,
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#333', mb: 3, textAlign: 'center' }}>
                  Welcome to {fleet.account_name || 'Vehicle Reservations'}
                </Typography>
                <Box sx={{ maxWidth: '600px', margin: '0 auto' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#00B894', mb: 2 }}>
                    How to Reserve:
                  </Typography>
                  <Box component="ol" sx={{ pl: 2, '& li': { mb: 2, fontSize: '16px', lineHeight: 1.6 } }}>
                    {fleets.length > 1 && (
                      <li>
                        <strong>Select Your Fleet:</strong> Choose the fleet location from the dropdown on the left.
                      </li>
                    )}
                    <li>
                      <strong>Pick Your Dates:</strong> Use the calendar to select your rental start and end dates.
                    </li>
                    <li>
                      <strong>Find Vehicles:</strong> Click the "Find Available Vehicles" button to see what's available for your dates.
                    </li>
                    <li>
                      <strong>Choose Your Vehicle:</strong> Browse the available options and select the vehicle type that suits your needs.
                    </li>
                    <li>
                      <strong>Review & Reserve:</strong> Review the pricing details, add any optional extras, and complete your reservation.
                    </li>
                  </Box>
                  <Box 
                    sx={{ 
                      mt: 4, 
                      p: 2, 
                      backgroundColor: 'rgba(0,184,148,0.1)', 
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}
                  >
                    <Typography variant="body1" sx={{ color: '#555', fontStyle: 'italic' }}>
                      Ready to get started? {fleets.length > 1 ? 'Select your fleet and dates' : 'Select your dates'} to begin!
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (availableVehicleTypes.length > 0 || availableAssetTypes.length > 0) && !selectedVehicleType && !selectedAsset ? (
              <Box
                sx={{
                  width: '100%',
                  backgroundColor: '#ffffff',
                  borderRadius: '10px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  p: isMobile ? 1 : 3,
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#333', mb: 3, textAlign: 'center' }}>
                  {availableVehicleTypes.length > 0 ? 'Available Vehicles' : 'Available Options'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {availableVehicleTypes.map((availableType) => {
                    const vehicleType = vehicleTypes.find(vt => vt.vehicle_type_id === availableType.vehicle_type_id);
                    // Plain-text name for accessibility (img alt) and fallback.
                    const displayName = availableType.vehicle_type_name || vehicleType?.vehicle_type_name || 'Unknown Vehicle';
                    // Badge (admin-configurable, may contain HTML) is the visible card
                    // label; it defaults to the vehicle type name server-side.
                    const badgeHtml = availableType.badge || '';
                    const vehicleImage = vehicleType?.features?.vehicle_image || '';
                    const vehicleDescription = vehicleType?.features?.vehicle_type_description || '';
                    
                    if (!vehicleType && !availableType.vehicle_type_name) return null;
                    
                    return (
                      <Box
                        key={availableType.vehicle_type_id}
                        onClick={() => handleVehicleSelection(availableType.vehicle_type_id)}
                        sx={{
                          cursor: 'pointer',
                          border: '2px solid #e0e0e0',
                          borderRadius: '12px',
                          padding: isMobile ? '9px' : '20px',
                          display: 'flex',
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: isMobile ? 'stretch' : 'center',
                          gap: isMobile ? 1.5 : 3,
                          transition: 'all 0.3s ease',
                          backgroundColor: '#fff',
                          '&:hover': {
                            borderColor: '#00B894',
                            boxShadow: '0 4px 12px rgba(0,184,148,0.2)',
                            transform: isMobile ? 'none' : 'translateX(8px)'
                          }
                        }}
                      >
                        {/* Image and Arrow Container - side by side on mobile */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: isMobile ? 'row' : 'column',
                          alignItems: 'center',
                          gap: isMobile ? 2 : 0,
                          width: isMobile ? '100%' : 'auto'
                        }}>
                          <Box
                            sx={{
                              width: isMobile ? '120px' : '150px',
                              height: isMobile ? '80px' : '100px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <img 
                              src={vehicleImage} 
                              alt={displayName}
                              style={{ 
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                              }} 
                            />
                          </Box>
                          
                          {/* Reserve Now button - shown below image on mobile */}
                          {isMobile && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#00B894',
                                color: 'white',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                fontWeight: 600,
                                fontSize: '14px',
                                boxShadow: '0 2px 8px rgba(0,184,148,0.3)',
                                flexShrink: 0,
                                '&:hover': {
                                  backgroundColor: '#00A983'
                                }
                              }}
                            >
                              Reserve Now
                            </Box>
                          )}
                        </Box>
                        
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                            {badgeHtml ? (
                              <Typography
                                variant="h6"
                                component="div"
                                sx={{ fontWeight: 600, color: '#333' }}
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(badgeHtml) }}
                              />
                            ) : (
                              <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                                {displayName}
                              </Typography>
                            )}
                            {availableType.daily_price_cents > 0 && (
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#00B894' }}>
                                ${(availableType.daily_price_cents / 100).toFixed(0)}/day
                              </Typography>
                            )}
                          </Box>
                          {vehicleDescription && (
                            <Typography
                              variant="body2"
                              component="div"
                              sx={{
                                color: '#666',
                                mb: 0.5,
                                fontStyle: 'italic'
                              }}
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(vehicleDescription) }}
                            />
                          )}
                          
                          {/* Quantity dropdown - only show if more than 1 available */}
                          {availableType.available_count > 1 && (
                            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#555' }}>
                                Quantity:
                              </Typography>
                              <Select
                                value={vehicleQuantities[availableType.vehicle_type_id] || 1}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setVehicleQuantities(prev => ({
                                    ...prev,
                                    [availableType.vehicle_type_id]: e.target.value
                                  }));
                                }}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                                sx={{
                                  minWidth: '80px',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00B894'
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00A983'
                                  }
                                }}
                              >
                                {Array.from({ length: availableType.available_count }, (_, i) => i + 1).map(num => (
                                  <MenuItem key={num} value={num}>{num}</MenuItem>
                                ))}
                              </Select>
                            </Box>
                          )}
                          
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              color: availableType.available_count > 0 ? '#00B894' : '#ff5252',
                              fontWeight: 600
                            }}
                          >
                            {availableType.available_count} Available
                          </Typography>
                        </Box>
                        
                        {/* Reserve Now button - only shown on desktop (on right side) */}
                        {!isMobile && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#00B894',
                              color: 'white',
                              padding: '10px 20px',
                              borderRadius: '8px',
                              fontWeight: 600,
                              fontSize: '16px',
                              boxShadow: '0 2px 8px rgba(0,184,148,0.3)',
                              minWidth: '120px',
                              '&:hover': {
                                backgroundColor: '#00A983'
                              }
                            }}
                          >
                            Reserve Now
                          </Box>
                        )}
                      </Box>
                    );
                  })}

                  {/* ACOS: asset offering cards (unmonetized, no price). Rendered after any vehicle
                      cards so an asset-only fleet (fleet 34) still surfaces bookable options. */}
                  {availableAssetTypes.map((offering) => (
                    <Box
                      key={`asset-${offering.operator_reservation_id}`}
                      onClick={() => handleAssetSelection(offering)}
                      sx={{
                        cursor: 'pointer',
                        border: '2px solid #e0e0e0',
                        borderRadius: '12px',
                        padding: isMobile ? '9px' : '20px',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center',
                        gap: isMobile ? 1.5 : 3,
                        transition: 'all 0.3s ease',
                        backgroundColor: '#fff',
                        '&:hover': {
                          borderColor: '#00B894',
                          boxShadow: '0 4px 12px rgba(0,184,148,0.2)',
                          transform: isMobile ? 'none' : 'translateX(8px)'
                        }
                      }}
                    >
                      {(offering.image_url_large || offering.image_url_small) && (
                        <Box
                          sx={{
                            width: isMobile ? '120px' : '150px',
                            height: isMobile ? '80px' : '100px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <img
                            src={offering.image_url_large || offering.image_url_small}
                            alt={offering.asset_type_name || 'Reservation'}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        </Box>
                      )}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
                            {offering.asset_type_name || 'Reservation'}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#00B894' }}>
                            No charge
                          </Typography>
                        </Box>
                        {offering.description && (
                          <Typography variant="body2" sx={{ color: '#666', mb: 0.5, fontStyle: 'italic' }}>
                            {offering.description}
                          </Typography>
                        )}
                        <Typography
                          variant="body1"
                          sx={{
                            color: offering.available_count > 0 ? '#00B894' : '#ff5252',
                            fontWeight: 600
                          }}
                        >
                          {offering.available_count} Available
                        </Typography>
                      </Box>

                      {!isMobile && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#00B894',
                            color: 'white',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            fontWeight: 600,
                            fontSize: '16px',
                            boxShadow: '0 2px 8px rgba(0,184,148,0.3)',
                            minWidth: '120px',
                            '&:hover': { backgroundColor: '#00A983' }
                          }}
                        >
                          Reserve Now
                        </Box>
                      )}
                      {isMobile && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#00B894',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontWeight: 600,
                            fontSize: '14px',
                            boxShadow: '0 2px 8px rgba(0,184,148,0.3)',
                            mt: 1.5,
                            '&:hover': { backgroundColor: '#00A983' }
                          }}
                        >
                          Reserve Now
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : vehicleTypeError && dateRangeSelected ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ 
                  width: '100%', 
                  textAlign: 'center', 
                  minHeight: '400px',
                  position: 'relative',
                  backgroundColor: '#ffffff',
                  borderRadius: '10px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  p: 4,
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff3cd',
                    border: '2px solid #ffc107',
                    borderRadius: '10px',
                    padding: '24px',
                    maxWidth: '500px',
                    margin: '0 auto'
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#856404',
                      fontWeight: 600,
                      marginBottom: '16px'
                    }}
                  >
                    No Vehicles Available
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#856404',
                      marginBottom: '16px',
                      lineHeight: 1.6
                    }}
                  >
                    {vehicleTypeError}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#856404',
                      fontStyle: 'italic'
                    }}
                  >
                    Please try selecting different dates or contact {fleet.account_name || 'the operator'} for assistance.
                  </Typography>
                </Box>
              </Box>
            ) : selectedVehicleType ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="flex-start"
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  height: '100%',
                  position: 'relative',
                  backgroundColor: '#ffffff',
                  borderRadius: '10px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  p: isMobile ? 1 : 3,
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                {/* Vehicle image is always shown when a vehicle is selected */}
                {isMobile === false && (
                  <div style={{ 
                    width: '100%', 
                    display: 'flex', 
                    justifyContent: 'center', 
                    overflow: 'visible',
                    marginTop: '10px',
                    marginBottom: '30px'
                  }}>
                    <img
                      src={vehicleImage}
                      alt="Vehicle"
                      crossOrigin="anonymous"
                      style={{
                        height: 'auto',
                        maxWidth: '90%',
                        maxHeight: '400px',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        padding: '10px'
                      }}
                    />
                  </div>
                )}

                <Typography
                  variant="h5" 
                  component="div" 
                  style={{ 
                    color: '#333', 
                    fontSize: isMobile ? 18 : 24,
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '8px'
                  }}
                >
                  {selectedVehicleName}
                </Typography>

                {/* Rental details are conditionally shown based on date selection */}
                {/* ACOS: unmonetized fleets show a "No payment required" affordance instead of a price breakdown. */}
                {!isMonetized && dateRangeSelected && selectedStartDate && selectedEndDate ? (
                  <Box
                    sx={{
                      textAlign: 'center',
                      width: '100%',
                      background: 'rgba(0,184,148,0.1)',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}
                  >
                    <Typography
                      component="div"
                      sx={{
                        fontSize: isMobile ? 16 : 18,
                        fontWeight: 600,
                        color: '#00B894',
                      }}
                    >
                      No payment required
                    </Typography>
                    <Typography
                      component="div"
                      sx={{ fontSize: isMobile ? 13 : 14, color: '#555', marginTop: '4px' }}
                    >
                      This reservation is free — no charge will be made.
                    </Typography>
                  </Box>
                ) : dateRangeSelected && selectedStartDate && selectedEndDate && priceDetails ? (
                  <RentalDetails
                    priceDetails={priceDetails}
                    selectedQuantity={selectedQuantity}
                    selectedStartDate={selectedStartDate}
                    selectedEndDate={selectedEndDate}
                    couponPercent={couponPercent}
                    isMobile={isMobile}
                    noteContent={hasNote ? noteContent : null}
                  />
                ) : (
                  <Box
                    sx={{
                      textAlign: 'center',
                      width: '100%',
                      background: 'rgba(0,184,148,0.1)',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}
                  >
                    <Typography
                      component="div"
                      sx={{
                        fontSize: isMobile ? 16 : 18,
                        fontWeight: 500,
                        color: '#00B894',
                      }}
                    >
                      Please select a date range
                    </Typography>
                  </Box>
                )}
                
                {selectedVehicle && selectedVehicle.features && selectedVehicle.features.reservation_description && (
                  <Typography
                    component="div"
                    style={{
                      color: '#555',
                      fontSize: isMobile ? 14 : 16,
                      textAlign: 'left',
                      width: '100%',
                      padding: isMobile ? '8px' : '10px',
                      background: '#f9f9f9',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      boxSizing: 'border-box'
                    }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedVehicle.features.reservation_description) }}
                  />
                )}                <Button
                  onClick={handleReserveClick}
                  variant="contained"
                  color="primary"
                  sx={{
                    backgroundColor: '#00B894',
                    color: 'white',
                    borderRadius: '30px',
                    height: '48px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    outline: 'none',
                    border: 'none',
                    alignSelf: 'center',
                    marginTop: '16px',
                    padding: '0 32px',
                    fontSize: '16px',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(0,184,148,0.3)',
                    '&:hover': {
                      backgroundColor: '#00A983',
                      boxShadow: '0 6px 16px rgba(0,184,148,0.4)',
                    },
                  }}
                >                  RESERVE NOW
                </Button>
              </Box>
            ) : selectedAsset && (
              /* ACOS: selected asset offering detail. Unmonetized — always a "no charge" panel. */
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="flex-start"
                sx={{
                  width: '100%',
                  textAlign: 'center',
                  height: '100%',
                  position: 'relative',
                  backgroundColor: '#ffffff',
                  borderRadius: '10px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  p: isMobile ? 1 : 3,
                  border: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                <Typography
                  variant="h5"
                  component="div"
                  style={{
                    color: '#333',
                    fontSize: isMobile ? 18 : 24,
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '8px'
                  }}
                >
                  {selectedAsset.asset_type_name || 'Reservation'}
                </Typography>

                {(selectedAsset.image_url_large || selectedAsset.image_url_small) && (
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: isMobile ? '220px' : '320px',
                      height: isMobile ? '140px' : '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px'
                    }}
                  >
                    <img
                      src={selectedAsset.image_url_large || selectedAsset.image_url_small}
                      alt={selectedAsset.asset_type_name || 'Reservation'}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                )}

                {dateRangeSelected && selectedStartDate && selectedEndDate ? (
                  <Box
                    sx={{
                      textAlign: 'center',
                      width: '100%',
                      background: 'rgba(0,184,148,0.1)',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}
                  >
                    <Typography component="div" sx={{ fontSize: isMobile ? 16 : 18, fontWeight: 600, color: '#00B894' }}>
                      No payment required
                    </Typography>
                    <Typography component="div" sx={{ fontSize: isMobile ? 13 : 14, color: '#555', marginTop: '4px' }}>
                      This reservation is free — no charge will be made.
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      textAlign: 'center',
                      width: '100%',
                      background: 'rgba(0,184,148,0.1)',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}
                  >
                    <Typography component="div" sx={{ fontSize: isMobile ? 16 : 18, fontWeight: 500, color: '#00B894' }}>
                      Please select a date range
                    </Typography>
                  </Box>
                )}

                {selectedAsset.description && (
                  <Typography
                    component="div"
                    style={{
                      color: '#555',
                      fontSize: isMobile ? 14 : 16,
                      textAlign: 'left',
                      width: '100%',
                      padding: isMobile ? '8px' : '10px',
                      background: '#f9f9f9',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      boxSizing: 'border-box'
                    }}
                  >
                    {selectedAsset.description}
                  </Typography>
                )}

                <Button
                  onClick={handleReserveClick}
                  variant="contained"
                  color="primary"
                  disabled={!dateRangeSelected}
                  sx={{
                    backgroundColor: '#00B894',
                    color: 'white',
                    borderRadius: '30px',
                    height: '48px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                    outline: 'none',
                    border: 'none',
                    alignSelf: 'center',
                    marginTop: '16px',
                    padding: '0 32px',
                    fontSize: '16px',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(0,184,148,0.3)',
                    '&:hover': {
                      backgroundColor: '#00A983',
                      boxShadow: '0 6px 16px rgba(0,184,148,0.4)',
                    },
                    '&:disabled': { backgroundColor: '#cccccc', color: '#666' }
                  }}
                >
                  RESERVE NOW
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      <LoginDialog
        open={isLoginDialogOpen}
        onClose={() => setIsLoginDialogOpen(false)}
        title="Verify to finish your booking"
        requirePayment={isMonetized}
        // ACOS: per-fleet auth config drives phone / email / OR chooser (null ⇒ phone).
        authMethods={fleet.reservation_auth_methods || 'phone'}
        fleetId={fleet.id}
        // Fleet brand primary for the dialog CTA + accents (navy for BallyQ), sourced from the
        // fleet object in localStorage: ui_color1, else menu_bkg_color, else a navy fallback.
        primaryColor={fleet.ui_color1 || fleet.menu_bkg_color || '#004990'}
        successCallback={async ({ user, user_id, waiver_status, stripe_customer_id, operator, loadStripe }) => {
          try {
            const fleet = JSON.parse(localStorage.getItem('fleet'));
            // ACOS: unmonetized fleets skip payment entirely. Read from state (set on fleet load)
            // with a defensive fallback to the stored fleet in case state has not settled.
            const unmonetized = fleet && fleet.is_monetized === false ? true : !isMonetized;

            // Safety check for priceDetails — only required on the monetized (paid) path.
            if (!unmonetized && !priceDetails) {
              console.error('priceDetails is not available - this should not happen');
              throw new Error('Pricing information is not available. Please try selecting your dates again.');
            }

            // ACOS: an asset offering carries its own increment; a vehicle uses the window's.
            const effectiveIncrement = selectedAsset
              ? (selectedAsset.min_increment_minutes || minIncrement)
              : minIncrement;

            // Create simplified params object for new API
            // Backend now handles: lookups, hash generation, pricing calculation, receipt creation, success_url.
            // ACOS: send vehicle_type_id OR asset_type_id (+resolved offering id) so the create
            // endpoint books the correct offering. Asset offerings are always unmonetized.
            const params = {
              amount: priceDetails ? priceDetails.amount : 0, // Total amount for validation (0 when unmonetized)
              user_id,
              fleet_id: fleet.id,
              vehicle_type_id: selectedAsset ? null : selectedVehicleType,
              asset_type_id: selectedAsset ? selectedAsset.asset_type_id : null,
              reservation_id: selectedAsset ? selectedAsset.operator_reservation_id : null,
              start_date: startDate,
              end_date: endDate,
              coupon_id: hasCoupon ? couponId : null,
              addon_ids: selectedAddons.length > 0 ? selectedAddons : null,
              note: noteContent || null,
              quantity: selectedQuantity,
              min_increment_minutes: effectiveIncrement, // ACOS: 60 for hourly; null -> daily
              duration_mode: durationMode // ACOS: hourly-mode fleets bill by duration (daily = ceil(hours/24))
            };

            // Store minimal params in localStorage for potential retry / waiver-flow resume.
            // ACOS: the `unmonetized` flag tells the waiver flow to create-then-confirm (no Stripe).
            // Display name + image for the confirmation page (survives the waiver hop). An asset
            // offering (e.g. Scissor Lift) carries its own name/image; a vehicle uses its type name
            // + first vehicle image. Without these the /checkout page shows "Unknown Vehicle" + a
            // broken image for the waiver-gated free-booking path.
            const bookingName = selectedAsset
              ? (selectedAsset.asset_type_name || fleet.account_name || 'Reservation')
              : (selectedVehicleName || fleet.account_name || 'Reservation');
            const bookingImage = selectedAsset
              ? (selectedAsset.image_url_large || selectedAsset.image_url_small || null)
              : ((Array.isArray(vehicleImage) ? vehicleImage[0] : vehicleImage) || null);

            const reservationParams = {
              params,
              stripe_pk: operator.stripe_pk,
              unmonetized,
              operator_id: operator.id,
              booking_name: bookingName,
              image_url: bookingImage
            };

            localStorage.setItem('reservationParams', JSON.stringify(reservationParams));

            // Check if operator has enhanced_waiver feature AND waiver_status is null or rejected.
            // This gate is honored on BOTH paths — the waiver still comes before confirmation.
            if (operator.features && operator.features.includes('enhanced_waiver') && (waiver_status === null || waiver_status === 'rejected')) {
              setIsLoginDialogOpen(false);
              navigate(`/waiver?user_id=${user_id}&operator=${operator.id}&fleet_id=${fleet.id}&payment_flow=true`);
            } else if (unmonetized) {
              // ACOS unmonetized: no waiver required — create the free reservation and go straight
              // to the existing /checkout confirmation page (no Stripe).
              try {
                const created = await createReservation({
                  user_id,
                  fleet_id: fleet.id,
                  operator_id: operator.id,
                  start_date: startDate,
                  end_date: endDate,
                  // ACOS: send vehicle_type_id OR asset_type_id (+resolved offering id).
                  vehicle_type_id: selectedAsset ? null : (selectedVehicleType || null),
                  asset_type_id: selectedAsset ? selectedAsset.asset_type_id : null,
                  reservation_id: selectedAsset ? selectedAsset.operator_reservation_id : null,
                  min_increment_minutes: effectiveIncrement,
                  note: noteContent || null
                });
                setIsLoginDialogOpen(false);
                // Reuse the name/image computed above so the confirmation page shows the offering
                // (e.g. Scissor Lift) + its image instead of "Unknown Vehicle" / a broken image.
                const confirmUrl = 'checkout?operator=' + operator.id
                  + '&fleet=' + fleet.id
                  + '&reservation_amount=0.00'
                  + '&vehicle_name=' + encodeURIComponent(bookingName)
                  + (bookingImage ? '&vehicle_image_url=' + encodeURIComponent(bookingImage) : '')
                  + '&start_date=' + encodeURIComponent(created.start_date || startDate)
                  + '&end_date=' + encodeURIComponent(created.end_date || endDate)
                  + '&reservation_hash=' + encodeURIComponent(created.reservation_hash || '')
                  + (created.created_date ? '&created_date=' + encodeURIComponent(created.created_date) : '')
                  + '&base_charge=0&taxes=0&daily_rate=0&coupon_percent=0'
                  + '&note=' + encodeURIComponent(noteContent || '')
                  + '&quantity=1';
                localStorage.removeItem('reservationParams');
                navigate('/' + confirmUrl);
              } catch (createError) {
                console.error('Unmonetized reservation create error:', createError);
                throw createError;
              }
            } else {
              try {
                const session = await createCheckoutSession(params);
                
                const stripe = await loadStripe(operator.stripe_pk);
                console.log('Stripe loaded, redirecting to checkout');
                
                const { error } = await stripe.redirectToCheckout({ sessionId: session.id });
                if (error) {
                  console.error('Stripe redirect error:', error);
                  throw new Error('Failed to redirect to payment. Please try again.');
                }
              } catch (checkoutError) {
                // Handle specific error cases from the new API
                if (checkoutError.message && checkoutError.message.includes('Price Mismatch')) {
                  console.error('Price mismatch detected:', checkoutError);
                  throw new Error('The pricing has changed. Please review the updated price and try again.');
                } else if (checkoutError.message && checkoutError.message.includes('User Not Found')) {
                  throw new Error('User account not found. Please try logging in again.');
                } else if (checkoutError.message && checkoutError.message.includes('Fleet Not Found')) {
                  throw new Error('Fleet information not found. Please refresh and try again.');
                } else if (checkoutError.message && checkoutError.message.includes('Vehicle Type Not Found')) {
                  throw new Error('Selected vehicle type is no longer available. Please select a different vehicle.');
                } else {
                  console.error('Checkout error:', checkoutError);
                  throw checkoutError;
                }
              }
            }
          } catch (error) {
            console.error('Error in reservations success callback:', error);
            // The LoginDialog component will handle this error and show it to the user
            throw error;
          }
        }}
      />
    </div>
  );
};

export default ReservationPage;
